// „Как е платено" — разбивката на оборота по метод на плащане.
//
// Това е първото, което се сверява с касата в края на смяната, и старият
// дашборд го носеше. Числата идват от `closed_today.methods` — както е ПЛАТЕНА
// сметката, тоест смесеното плащане е собствен метод с пълната си сума, а не
// разпределено между в брой и карта (там разпределението е в `cash_amount` /
// `card_amount` и би дало друг сбор).
//
// Имената на методите идват от справочника, а не от отговора: методите се
// преименуват от конфигурацията и „метод #7" не значи нищо на екрана.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../../utils/currency";
import { parseDecimal } from "../../../../components/dataTable";
import type { PaymentMethod } from "../../../../api/paymentMethods";
import type { DashboardClosedToday } from "../../../../api/reportsDashboard";
import { pluralLabel } from "../reportPlural";

type Props = {
  closed: DashboardClosedToday;
  paymentMethods: PaymentMethod[];
};

/** Тоновете на лентата — по ред на дела, не по метод: методите са динамични. */
const SHARE_TONE = [
  "bg-brand",
  "bg-brand-deep",
  "bg-info",
  "bg-ok",
  "bg-ghost",
];

export const PaymentMixCard = ({ closed, paymentMethods }: Props) => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith("en");

  const nameById = new Map<number, string>();
  for (const method of paymentMethods) {
    nameById.set(method.id, isEnglish ? method.name_en : method.name_bg);
  }

  const total = parseDecimal(closed.total_amount);
  const rows = (closed.methods ?? [])
    .map((method) => ({
      id: method.payment_method_id,
      name:
        nameById.get(method.payment_method_id) ??
        t("reportsPage.dashPaymentOther"),
      amount: parseDecimal(method.amount),
      count: Math.round(parseDecimal(method.count)),
    }))
    .filter((row) => row.amount !== 0 || row.count !== 0)
    .sort((a, b) => b.amount - a.amount);

  return (
    <section className="flex flex-col rounded-xl border border-shade-2 bg-surface px-4 py-3.5">
      <header className="mb-3 flex flex-wrap items-baseline gap-2.5">
        <h2 className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
          {t("reportsPage.dashPaymentMixTitle")}
        </h2>
        <p className="text-[12px] font-semibold text-soft">
          {pluralLabel(
            t,
            closed.total_count,
            "reportsPage.billsHistoryTableCountOne",
            "reportsPage.billsHistoryTableCount",
          )}
        </p>
      </header>

      {rows.length && total > 0 ? (
        <>
          <div className="mb-3 flex h-2 overflow-hidden rounded-full bg-fill">
            {rows.map((row, index) => (
              <span
                key={row.id}
                aria-hidden
                className={SHARE_TONE[index % SHARE_TONE.length]}
                // Делът е известен чак по време на изпълнение — единствената
                // inline стойност тук.
                style={{ width: `${(row.amount / total) * 100}%` }}
              />
            ))}
          </div>

          <ul className="flex flex-col gap-1.5">
            {rows.map((row, index) => (
              <li key={row.id} className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className={`size-[9px] shrink-0 rounded-full ${
                    SHARE_TONE[index % SHARE_TONE.length]
                  }`}
                />
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
                  {row.name}
                </span>
                <span className="shrink-0 font-pos-mono text-[11px] font-semibold tabular-nums text-muted">
                  {row.count}
                </span>
                <span className="shrink-0 font-pos-mono text-[13px] font-bold tabular-nums text-ink">
                  {fmtEuro(row.amount)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-[12.5px] font-medium text-muted">
          {t("reportsPage.dashPaymentMixEmpty")}
        </p>
      )}
    </section>
  );
};
